import dayjs from "dayjs/esm";
import customParseFormat from "dayjs/esm/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const DATE_FORMATS = ["DD.MM.YYYY", "YYYY-MM-DD", "DDMMYYYY"];

export class DateTimeFormatter {
  static formatDateToString(date: Date | null): string {
    if (!date)
      return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  static formatDateToDateTime(date: Date | null): string {
    if (!date)
      return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  static formatDateToLocalString(date: Date | null | undefined): string {
    if (!date)
      return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${day}.${month}.${year}`;
  }

  static formatDate(date: string | Date): string {
    if (!date)
      return "";
    const dt = new Date(date);
    const month = (dt.getMonth() + 1).toString().padStart(2, "0");
    const day = dt.getDate().toString().padStart(2, "0");
    return `${day}/${month}/${dt.getFullYear()}`;
  }

  static formatDateToISO(date: string | null | undefined): string {
    if (!date)
      return "";
    const parsed = DATE_FORMATS.map(f => dayjs(date, f, true)).find(d => d.isValid());
    return parsed ? parsed.format("YYYY-MM-DDTHH:mm:ss") : "";
  }

  static formatCustomOrISOToISO(date: string | null | undefined): string {
    if (!date)
      return "";
    const formats = ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD", "DD.MM.YYYY HH:mm", "DD.MM.YYYY", "DDMMYYYY", "DDMMYYYYHHmm"];
    const parsed = formats.map(f => dayjs(date, f, true)).find(d => d.isValid());
    return parsed ? parsed.format("YYYY-MM-DDTHH:mm:ss") : "";
  }

  static isISOFormat(date: string): boolean {
    const isoRegex = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
    return isoRegex.test(date);
  }

  static safeParseDate(dateString: string | null | undefined, format: string): string | null {
    if (!dateString)
      return null;
    const formats = ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD", "DD.MM.YYYY HH:mm", "DD.MM.YYYY", format];
    const parsed = formats.map(f => dayjs(dateString, f, true)).find(d => d.isValid());
    return parsed ? parsed.format("DD.MM.YYYY HH:mm") : null;
  }

  static safeParseSimpleDate(dateString: string | null | undefined): string | null {
    if (!dateString)
      return null;
    const validFormats = ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm:ss.SSS", "YYYY-MM-DDTHH:mm:ss.SSSSSS", "YYYY-MM-DD", "DD.MM.YYYY", "DDMMYYYY"];
    const parsed = validFormats.map(f => dayjs(dateString, f, true)).find(d => d.isValid());
    return parsed ? parsed.format("YYYY-MM-DD") : null;
  }

  static toDisplayDate(dateString: string | null | undefined): string | null {
    if (!dateString)
      return null;

    const match = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match)
      return match[1];

    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  }

  static safeParseShortDate(dateString: string | null | undefined): string | null {
    if (!dateString)
      return null;
    const validFormats = ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD", "DD.MM.YYYY", "DDMMYYYY"];
    const parsed = validFormats.map(f => dayjs(dateString, f, true)).find(d => d.isValid());
    return parsed ? parsed.format("DD.MM.YYYY") : null;
  }

  static parseDate(date: string): Date {
    if (!date)
      return new Date();

    const parsed = DATE_FORMATS
      .map(f => dayjs(date, f, true))
      .find(d => d.isValid());

    return parsed ? parsed.toDate() : new Date();
  }

  static addDaysSafe(date: Date, days: number): Date {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  }

  // Used for dd.MM.yyyy
  static addOneDay(dateStr: string): string {
    const dateParsed = this.safeParseShortDate(dateStr) ?? "";
    const parts = dateParsed.split(".").filter(Boolean);
    const [day, month, year] = parts.map(Number);

    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + 1);

    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();

    return `${d}${m}${y}`;
  }
}
